import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const ACTION_COLOR = {
  login: "text-blue-700 bg-blue-50 border-blue-200",
  logout: "text-slate-600 bg-slate-50 border-slate-200",
  user_create: "text-emerald-700 bg-emerald-50 border-emerald-200",
  user_update: "text-slate-700 bg-slate-50 border-slate-200",
  user_delete: "text-red-700 bg-red-50 border-red-200",
  job_create: "text-emerald-700 bg-emerald-50 border-emerald-200",
  job_update: "text-slate-700 bg-slate-50 border-slate-200",
  job_delete: "text-red-700 bg-red-50 border-red-200",
  job_bulk_delete: "text-red-700 bg-red-50 border-red-200",
  job_commit: "text-orange-700 bg-orange-50 border-orange-200",
  file_upload: "text-blue-700 bg-blue-50 border-blue-200",
  file_download: "text-slate-700 bg-slate-50 border-slate-200",
  file_delete: "text-red-700 bg-red-50 border-red-200",
};

function ActionPill({ action }) {
  const cls = ACTION_COLOR[action] || "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border ${cls}`}>
      {action}
    </span>
  );
}

export default function AdminAuditPage() {
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/audit-logs`);
        setLogs(data.logs || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <header className="border-b border-border bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-3">
          <Button size="icon" variant="ghost" onClick={() => nav("/")}
            className="h-9 w-9 hover:bg-slate-100 rounded-md">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h1 className="font-heading font-bold text-lg tracking-tight">Audit log</h1>
          <span className="text-sm text-slate-500 font-medium">
            {loading ? "" : `${logs.length} recent event${logs.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Trail</p>
          <h2 className="font-heading text-4xl font-black tracking-tighter">Every action, timestamped.</h2>
        </div>

        {loading ? (
          <div className="space-y-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-slate-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="border-2 border-dashed border-border rounded-md px-8 py-16">
            <p className="text-slate-500 font-medium">No audit events yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead className="border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">When</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Who</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Action</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Target</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-600 whitespace-nowrap">
                      {new Date(l.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 truncate max-w-[220px] font-medium">
                      {l.user_email || l.user_id || "—"}
                    </td>
                    <td className="px-4 py-2"><ActionPill action={l.action} /></td>
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-500 truncate max-w-[180px]">
                      {l.target || "—"}
                    </td>
                    <td className="px-4 py-2 text-[12px] text-slate-500 truncate max-w-[320px]">
                      {l.meta && Object.keys(l.meta).length > 0
                        ? JSON.stringify(l.meta)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
